import React from 'react';
import wf from 'webfontloader';
import { darken, decomposeColor, lighten } from '@material-ui/core/styles/colorManipulator';
import { alpha } from '@material-ui/core/styles';
import {
  ConfigurationColors,
  ConfigurationModel,
  ConfigurationTheme,
  getPrefilledButtons,
} from '@tink/config/src/tinkLinkConfig';
import { ThemeProvider } from '@tink/core/src/components/providers/ThemeProvider/ThemeProvider';
import { captureMessage } from '@tink/core/src/sentry';
import { Helmet } from 'react-helmet';
import { useConfiguration } from '../ConfigurationProvider/ConfigurationProvider';
import { isMerchantNetflix } from '../../../models/configuration';
import { netflixTheme } from './netflixTheme';
import { TinkLinkTheme, tinkLinkTheme } from './tinkLinkTheme';
import { applyTinkLinkOverrides } from './tinkLinkMaterialThemeOverrides';

enum FontProvider {
  GOOGLE = 'google',
  TYPEKIT = 'typekit',
  CUSTOM = 'custom',
}

export const validateColor = (color: string, colorType: string): string => {
  const fallbackColor = '#f0f'; // purple

  try {
    decomposeColor(color);

    return color;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      captureMessage('Invalid color configuration', {
        custom: {
          color,
          colorType,
        },
      });
    }

    return fallbackColor;
  }
};

const getFontProvider = (theme: ConfigurationTheme): WebFont.Config | undefined => {
  const { sourceFont, provider, identifier, sourceUrl } = theme.fonts;

  switch (provider) {
    case FontProvider.GOOGLE:
      if (!sourceFont.length) {
        throw new Error(
          `Font configuration error: 'sourceFont' value missing for ${FontProvider.GOOGLE} provider`
        );
      }

      return { google: { families: sourceFont } };
    case FontProvider.TYPEKIT:
      if (!identifier) {
        throw new Error(
          `Font configuration error: 'identifier' value missing for ${FontProvider.TYPEKIT} provider`
        );
      }

      return { typekit: { id: identifier } };
    case FontProvider.CUSTOM:
      if (!sourceFont.length || !sourceUrl.length) {
        throw new Error(
          `Font configuration error: 'sourceFont' and 'sourceUrl' values missing for ${FontProvider.CUSTOM} provider`
        );
      }

      return { custom: { families: sourceFont, urls: sourceUrl } };
    default:
      return undefined;
  }
};

export const applyClientTheme = (
  configuration: ConfigurationModel,
  clientTheme?: ConfigurationTheme
): TinkLinkTheme => {
  if (!clientTheme) {
    return tinkLinkTheme;
  }

  if (clientTheme.fonts.provider) {
    let webFontConfig;
    try {
      webFontConfig = getFontProvider(clientTheme);
    } catch (error) {
      const { message } = error as Error;

      captureMessage(message);
    }

    if (webFontConfig) {
      wf.load(webFontConfig);
    }
  }

  const resultTheme: TinkLinkTheme = { ...tinkLinkTheme };

  if (clientTheme.fonts.fontFamily) {
    resultTheme.font.face = clientTheme.fonts.fontFamily;
  }

  if (clientTheme.typography) {
    resultTheme.typography = clientTheme.typography;
  }

  if (clientTheme.buttons) {
    resultTheme.configuration.buttons = getPrefilledButtons(clientTheme.buttons);
  }

  Object.keys(clientTheme.colors).forEach((colorType) => {
    const colorCode = clientTheme.colors[colorType as keyof ConfigurationColors];

    if (colorCode) {
      resultTheme.configuration[colorType as keyof ConfigurationColors] = validateColor(
        colorCode,
        colorType
      );
    }
  });

  if (!clientTheme.colors.buttonBackgroundColor && clientTheme.colors.primaryColor) {
    resultTheme.configuration.buttonBackgroundColor = clientTheme.colors.primaryColor;
  }

  // buttonSecondaryTextColor is not yet in the backend.
  // For customers who are using theming, primaryColor will be the default.
  if (!clientTheme.colors.buttonSecondaryTextColor) {
    resultTheme.configuration.buttonSecondaryTextColor = resultTheme.configuration.primaryColor;
  }

  const { primaryColor } = clientTheme.colors;

  if (primaryColor && primaryColor.match(/^#[0-9a-f]{3,6}$/i)) {
    resultTheme.palette.primary = {
      main: primaryColor,
      50: lighten(primaryColor, 0.9),
      100: lighten(primaryColor, 0.8),
      200: lighten(primaryColor, 0.6),
      300: lighten(primaryColor, 0.4),
      400: lighten(primaryColor, 0.2),
      500: primaryColor,
      600: darken(primaryColor, 0.2),
      700: darken(primaryColor, 0.4),
      800: darken(primaryColor, 0.6),
      900: darken(primaryColor, 0.8),
      A100: lighten(primaryColor, 0.8),
      A200: lighten(primaryColor, 0.6),
      A400: primaryColor,
      A700: darken(primaryColor, 0.4),
      contrastDefaultColor: 'light',
    };

    if (!clientTheme.colors.strokeColor) {
      resultTheme.configuration.strokeColor = alpha(primaryColor, 0.15);
    }
  }

  // this is just temporary for our merchant netflix
  if (isMerchantNetflix(configuration)) {
    resultTheme.typography = netflixTheme.typography;
    resultTheme.configuration.header = netflixTheme.header;
    resultTheme.configuration.textAlignment = netflixTheme.textAlignment;

    resultTheme.configuration.buttonSecondaryTextColor =
      netflixTheme.colors.buttonSecondaryTextColor;
  }

  return resultTheme;
};

export const ConfiguredThemeProvider: React.FC = ({ children }) => {
  const configuration = useConfiguration();
  const styledComponentsTheme = applyClientTheme(configuration.data, configuration.activeTheme);
  const materialUiThemeOverrides = applyTinkLinkOverrides(styledComponentsTheme);

  return (
    <ThemeProvider
      theme={styledComponentsTheme}
      materialUiThemeOverrides={materialUiThemeOverrides}
    >
      <Helmet>
        <meta name="theme-color" content={styledComponentsTheme.configuration.backgroundColor} />
      </Helmet>
      {children}
    </ThemeProvider>
  );
};
